import { type ReactNode, useMemo } from 'react'

import { Toast } from '@base-ui/react'

import { RequestError } from '@subaio/utils'

import { context, type SnackbarContext, type SnackbarOptions } from './context'
import { List } from './List'
import { SnackbarType } from './SnackbarType'
import { snackbarStore } from './store'
import { Viewport } from './Viewport'

type Props = {
	children: ReactNode
}

export const SnackbarProvider = ({ children }: Props) => {
	const contextValue = useMemo<SnackbarContext>(() => {
		const showSnackbar = ({ id, message, duration = 7, type = SnackbarType.Mute }: SnackbarOptions) => {
			snackbarStore.add({ id, description: message, timeout: duration * 1000, type })
		}

		const handleSnackbars: SnackbarContext['handleSnackbars'] =
			(func, handlers?) =>
			async (...args) => {
				try {
					const message = await func(...args)
					if (message) showSnackbar({ message, type: SnackbarType.Success })
				} catch (error) {
					let handler = null
					if (error instanceof RequestError && handlers && error.request.statusCode in handlers) {
						handler = handlers[error.request.statusCode]!
						error.isHandled = true
					} else if (handlers && 'default' in handlers) {
						handler = handlers.default
					} else {
						handler = 'Unknown error'
					}

					const message =
						typeof handler === 'string' ? handler : typeof handler === 'function' ? await handler(...args) : null

					if (message) showSnackbar({ message, type: SnackbarType.Error })

					throw error
				}
			}

		return { showSnackbar, handleSnackbars }
	}, [])

	return (
		<context.Provider value={contextValue}>
			<Toast.Provider toastManager={snackbarStore}>
				{children}
				<Toast.Portal>
					<Viewport>
						<List />
					</Viewport>
				</Toast.Portal>
			</Toast.Provider>
		</context.Provider>
	)
}
